import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { ApiService } from '@app/core/services/api/api.service';
import {
  formatShippingAddressString,
  formattedDate,
  getDeliveryPromise,
  TABLES,
  formatShippingNameString,
  TimeUnits,
  modifyDateRangeValuesByColumn,
  formatBillTo,
} from 'src/utils';
import { EMPTY, Observable, Subject } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { OrderService } from '@app/services/order/order.service';
import { Order } from '@app/services/order/order.model';
import {
  ColumnSettings,
  RowAction,
  TableFilterType,
  tableMessages,
} from '@app/sharedModule/models/table-settings.model';
import { searchTableConfig } from './configs/search-table.config';
import { Filter, QueryParameters, SortField } from '@app/core/entities/query-parameters.class';
import { QueryPagingParameters } from '@app/core/entities/query-paging-parameters.class';
import { UtilService } from '@app/core/services/utils/util.service';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { FreeSearchParameters } from '@app/core/entities/free-search-parameters.class';
import { SidePanelService } from '@app/features/side-panel/side-panel.service';
import { TableService } from '@app/services/table/table.service';
import { PdqToastComponent } from '@app/storybook/components/toast/toast.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { ColorCoding, OrderStatus } from '@pdqdev/pdq-shared/dist/entities';
import { getDefaultSortField } from '@utils/shop-utils';
import { ShopService } from '@services/shops/shop.service';
import { getTargetOrderRoute } from '@utils/order.utils';
import { OPTION } from '@app/storybook/components/options-list/options-list.component';
import { assignColorCodingToRows } from '@utils/color-coding.utils';

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SearchResultsComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('input') input: ElementRef;
  readonly TABLES = TABLES;
  private sortFields: SortField[] | 'NONE';
  private tableFilters: Filter[];

  rowActions = [RowAction.JUMP_TO_TABLE];
  rows = [];
  temp = [];
  freeSearchInputControl = new UntypedFormControl();
  showFreeSearchSuggestions = false;
  showFreeSearchClearButton = false;
  totalCountOrders: number;
  searchParams: string;
  isSyncingOrders: boolean;
  loading = false;
  searchResults = [];
  ordersPerPage = 50;
  allPages = {};
  activePage = 1;
  totalPages = 1;
  filter = 'all';
  filterActive = false;
  searchToggleButtons = false;
  freeSearchValue: any = '';
  freeSearchSelectedField = 'orderNumber';
  rowSelectOptions = [];
  focusedOrderId: string | null = null;
  searchControl: UntypedFormControl;
  searchInput: string;
  noResults = false;
  shop;
  orders;
  colorCodingData: ColorCoding[];
  private destroyed$: Subject<any> = new Subject();
  latestSearches: OPTION[] = [];
  selectedColumns = [];
  readonly COLUMN_OPTIONS: ColumnSettings[] = searchTableConfig.filter((column) => column.canBeHide || !column.default);
  columns: ColumnSettings[] = [...searchTableConfig];
  constructor(
    public dialog: MatDialog,
    public router: Router,
    public apiService: ApiService,
    private orderService: OrderService,
    private cookieService: CookieService,
    private utilService: UtilService,
    private spService: SidePanelService,
    private tableService: TableService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private shopService: ShopService,
  ) {
    this.ordersPerPage = this.orderService.getOrderPerPage();
  }

  ngOnInit(): void {
    this.loadLocalStorageDefaultColumns();
    this.initLatestSearches();
    const orderNumberParam = this.route.snapshot.queryParams.orderNumber;
    const queryParam = this.route.snapshot.queryParams.query;
    const initialSearch = orderNumberParam || queryParam;
    this.searchControl = new UntypedFormControl(initialSearch || '');
    if (initialSearch) {
      this.search();
    }
    this.initShopService();
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.initSidePanelService();
  }

  initSidePanelService(): void {
    this.spService.focusedOrderId$.pipe(takeUntil(this.destroyed$)).subscribe(({ focusedId }) => {
      this.focusedOrderId = focusedId ?? null;
    });
  }

  initTableService(): void {
    this.tableService.popoverPressedRowId$.pipe(takeUntil(this.destroyed$)).subscribe((rowId) => {
      this.focusedOrderId = rowId;
    });
  }

  initShopService() {
    this.shopService.shop$.pipe(takeUntil(this.destroyed$)).subscribe((shop) => {
      this.colorCodingData = shop?.colorCoding;
      this.shop = shop;
    });
  }

  ngAfterViewInit(): void {
    this.initTableService();

    // freesearch server-side autocomplete
    this.freeSearchInputControl.valueChanges
      .pipe(
        debounceTime(300),
        switchMap((value) => this.getFreeSearchSuggestions(value)),
      )
      .subscribe((data) => {
        this.searchResults = data;
        this.showFreeSearchSuggestions = this.searchResults.length > 0;
      });
  }

  getFreeSearchSuggestions(event): Observable<any[]> {
    if (event.length === 0) {
      this.showFreeSearchClearButton = false;
      this.searchToggleButtons = false;
      this.showFreeSearchSuggestions = false;
      return EMPTY;
    }
    this.showFreeSearchClearButton = true;
    this.showFreeSearchSuggestions = true;
    this.searchToggleButtons = true;
    return this.orderService.getSearchOrders(
      new FreeSearchParameters(this.searchParams, [this.freeSearchSelectedField]),
    ) as Observable<any[]>;
  }

  sortColumns(sortOptions): void {
    this.columns = this.columns.map((column: ColumnSettings, i: number) => {
      if (i !== sortOptions.index) {
        return { ...column, sortDirection: null };
      }

      const sortDirection = sortOptions.sortDirection;
      const sortFieldNames: string[] = column.sortFieldName ? column.sortFieldName : [column.prop];

      this.sortFields = sortFieldNames.map((fieldName: string) => new SortField(fieldName, sortDirection));

      return {
        ...column,
        sortDirection,
      };
    });

    this.getPagedOrders(this.activePage - 1, this.ordersPerPage);
  }

  loadLocalStorageDefaultColumns(): void {
    const data = localStorage.getItem('search-columns');
    this.selectedColumns = data
      ? JSON.parse(data)
      : searchTableConfig
          .filter((column: ColumnSettings) => !column.canBeHide || column.default)
          .map((column: ColumnSettings) => column.prop);
    this.loadSelectedColumns();
  }

  loadSelectedColumns(): void {
    localStorage.setItem('search-columns', JSON.stringify(this.selectedColumns));
    this.columns = searchTableConfig.filter(
      (column: ColumnSettings) => !column.canBeHide || this.selectedColumns.includes(column.prop),
    );
  }

  createQueryParameters(
    page: number,
    sortFields: SortField[] | 'NONE' = [new SortField(getDefaultSortField(this.shopService.shopPlatform), 'ASC')],
  ): QueryParameters {
    const queryParameters = new QueryParameters();
    queryParameters.page = page;
    queryParameters.maxResults = this.ordersPerPage;
    queryParameters.anyAll = 'all';
    queryParameters.filters = [new Filter('deleted', 'IN', ['0'])];
    queryParameters.sortFields = sortFields == 'NONE' ? undefined : sortFields;
    return queryParameters;
  }

  createQueryPagingParameters(
    page: number,
    sortFields: SortField[] | 'NONE' = [new SortField(getDefaultSortField(this.shopService.shopPlatform), 'ASC')],
  ): QueryPagingParameters {
    const queryPagingParameters = new QueryPagingParameters();
    queryPagingParameters.filters = [new Filter('deleted', 'IN', ['0'])];
    queryPagingParameters.page = page;
    queryPagingParameters.maxResults = this.ordersPerPage;
    queryPagingParameters.sortFields = sortFields == 'NONE' ? undefined : sortFields;
    return queryPagingParameters;
  }

  async onTableActivate({ row }) {
    if (!row.id) {
      return;
    }

    this.spService.setFocusedOrderId(row.id);
  }

  processOrders(data?: any): void {
    const count = data?.count ?? this.orders?.length;
    this.totalPages = Math.ceil(count / this.ordersPerPage);
    this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
    this.rows = data.orders ? data.orders : data;
    this.totalCountOrders = count;
    this.rows.forEach((row) => {
      if (row.orderStatus?.includes(OrderStatus.DELETED)) {
        row.comment = 'Deleted';
      }
      if (row?.createdDate) {
        row.order_date = formattedDate(row.createdDate);
      }
      if (row?.customer) {
        row.customerFormated = formatBillTo(row, this.shop?.metadata?.isHideEmailInBillTo);
      }
      row.formattedShippingCityAndProvince = formatShippingAddressString(row);
      row.foramttedShippingName = formatShippingNameString(row);
      row.deliveryPromise = getDeliveryPromise(row);
      row.currentColorTooltipString =
        !row.colorCoding || row.colorCoding.includes('transparent')
          ? ''
          : row.colorCoding
              .map((colorValue) => {
                const current = this.colorCodingData?.find((colorData) => colorData.value === colorValue);
                return current?.label || current?.defaultName || '';
              })
              .join(', ');
    });
    this.temp = this.rows;
  }

  onChangeOrdersPerPage(ordersPerPage: number): void {
    this.ordersPerPage = ordersPerPage;
    this.cookieService.set('orderperpage_cookie', ordersPerPage.toString());
    this.activePage = 1;
    this.totalPages = Math.ceil(this.totalCountOrders / this.ordersPerPage);
    this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
    this.getPagedOrders(0, this.ordersPerPage);
  }

  onChangeTableFilter(columnIndex: number): void {
    this.tableFilters = [];
    this.sortFields = undefined;
    let fieldName;

    this.columns.forEach((column: ColumnSettings) => {
      if (!column.filterType || !column.filterOption || (!column.filterValue && !column.filterValueArray?.length)) {
        return;
      }

      fieldName = column.filterFields ? column.filterFields : column.prop;
      let filterValue = [column.filterValue];

      if (column.filterType === TableFilterType.COLOR) {
        filterValue = column.filterValueArray;
      } else if (column.filterOption === 'DATE RANGE' || column.filterOption === 'DATE RANGE STRICT') {
        filterValue = [];
        if (column.filterValueStart) {
          filterValue.push(column.filterValueStart);
        }
        if (column.filterValueEnd) {
          const endDate = new Date(Date.parse(column.filterValueEnd));
          endDate.setDate(endDate.getDate() + 1);
          modifyDateRangeValuesByColumn(column.prop, { endDate: endDate });
          filterValue.push(endDate.toISOString());
        }
      } else if (column.filterOption === 'LIKE' && !this.shop?.metadata?.applySortingOnFilters) {
        // if we have to add a sort field in the future, consider use id instead of createdDate for better performance
        this.sortFields = 'NONE';
      }

      this.tableFilters.push(new Filter(fieldName, column.filterOption, filterValue));
    });

    this.filterActive = fieldName ? true : false;
    this.activePage = 1;
    this.getPagedOrders(this.activePage - 1, this.ordersPerPage);
  }

  onTableJump(order: Order): void {
    const targetRoute = getTargetOrderRoute(order?.orderStatus);
    if (targetRoute && order?.id) {
      this.router.navigate([`/${targetRoute}`], {
        state: {},
        queryParams: {
          search: order.id,
        },
      });
    }
  }

  reSyncOrderClick(row, fromSidePanel?: boolean): void {
    this.isSyncingOrders = true;
    row = { ...row, isSyncing: true, spinning: true };
    if (fromSidePanel) {
      this.spService.setActiveOrder(row);
    }
    this.orderService
      .syncOrder(row.id)
      .then(() => {
        this.toastService.show(PdqToastComponent, {
          message: `Order #${row.orderNumber} synced`,
          mode: 'info',
          duration: TimeUnits._SECOND_IN_MS * 3,
        });
      })
      .finally(() => {
        row.spinning = false;
        this.isSyncingOrders = false;
        row = { ...row, isSyncing: false, spinning: false };
        if (fromSidePanel) {
          this.spService.setActiveOrder(row);
        }
      });
  }

  changeColorCoding({ order, colors }) {
    const orderIds: string[] = [order.id];
    const previousRows = this.rows;
    this.assignColorsCodingUpdate(orderIds.map((id) => ({ id, colorCoding: colors })));
    this.orderService.updateOrdersColorCoding(orderIds, colors).subscribe({
      next: (succededOrders) => {
        if (succededOrders && succededOrders?.length < orderIds.length) {
          const rowsToRevert = previousRows.filter(
            (row) => !succededOrders.some((succededOrder) => succededOrder.id === row.id),
          );
          this.assignColorsCodingUpdate(rowsToRevert);
        }
      },
      error: (error) => {
        this.assignColorsCodingUpdate(previousRows);
        this.toastService.showToast({
          message: `Failed to update color coding`,
          mode: 'danger',
        });
      },
    });
  }

  private assignColorsCodingUpdate(updatedOrders: Pick<Order, 'id' | 'colorCoding'>[]) {
    const newRows = assignColorCodingToRows(this.rows, updatedOrders, this.colorCodingData);

    const spOrder = this.spService.getActiveOrder();
    if (spOrder?.id) {
      const spColorCoding = updatedOrders.find((order) => order.id === spOrder?.id)?.colorCoding;
      this.spService.setActiveOrder({ ...spOrder, colorCoding: spColorCoding });
    }
    this.temp = [...newRows];
  }

  getFreeSearchFilter(): Filter {
    return new Filter(this.freeSearchSelectedField, 'IN', [this.freeSearchValue]);
  }

  getPagedOrders(page, maxsize): void {
    this.loading = true;
    const queryParameters = this.sortFields?.length
      ? this.createQueryParameters(page, this.sortFields)
      : this.createQueryParameters(page);

    if (this.freeSearchValue?.length > 0) {
      const filter = this.getFreeSearchFilter();
      queryParameters.filters = [filter];
    } else if (this.filter === 'multiple') {
      queryParameters.filters = [new Filter(this.freeSearchSelectedField, 'IN', this.freeSearchValue)];
    }

    if (this.tableFilters?.length) {
      queryParameters.filters = [...queryParameters.filters, ...this.tableFilters];
    }

    this.orderService
      .globalSearchOrders(this.searchParams)
      .then((data) => {
        this.processOrders(data);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  paginate(page: number): void {
    this.activePage = page + 1;
    this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
    this.getPagedOrders(page, this.ordersPerPage);
  }

  jumpToPage(page: number): void {
    if (!page) {
      alert('Please enter a valid number');
      return;
    }

    if (page > this.totalPages) {
      alert(`Please enter a Page no. less than ${this.totalPages}`);
      return;
    }

    this.activePage = page;
    this.getPagedOrders(this.activePage - 1, this.ordersPerPage);
  }

  chooseEmptyMessage() {
    return this.filterActive || this.filter == 'multiple'
      ? tableMessages.NO_RESULT_MESSAGE
      : tableMessages.EMPTY_MESSAGE;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  setSearchParam(value?: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { query: value },
      queryParamsHandling: 'merge',
    });
  }

  reset() {
    this.searchControl.setValue('');
    this.searchInput = null;
    this.noResults = false;
    this.orders = null;
    this.setSearchParam(null);
  }

  onRecentSearchClick(option: OPTION) {
    this.searchControl.setValue(option.value);
    this.searchInput = option.value;
    this.noResults = false;
    this.orders = null;
    this.search();
  }

  initLatestSearches() {
    this.apiService.get('order/latest-searches').subscribe(({ latestSearches }) => {
      console.log('latestSearches', latestSearches);
      this.latestSearches = latestSearches.map((value) => ({ value, label: value }));
    });
  }

  toggleColumn(): void {
    this.loadSelectedColumns();
  }

  async search() {
    this.searchInput = (this.searchControl.value || '').trim();
    this.setSearchParam(this.searchInput || undefined);
    if (!this.searchInput) {
      return;
    }

    this.noResults = false;
    this.loading = true;
    try {
      const data = await this.orderService.globalSearchOrders(this.searchInput);
      if (!data?.length) {
        this.noResults = true;
      }
      this.orders = data;
      this.loading = false;
      this.processOrders(this.orders);
      this.initLatestSearches();
    } catch (err) {
      console.error(err);
      this.toastService.showToast({ mode: 'danger', message: 'Failed to search orders' });
      this.loading = false;
      this.initLatestSearches();
    }
  }

  closeSidePanel(drawer) {
    drawer.close();
  }
}
